import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type GameFormatKey = 'hussel' | 'toss' | 'racket-trekken';

interface GameFormatForms {
  /** husselen / tossen / racket trekken */
  verb: string;
  /** hussel / toss / racket trekken */
  noun: string;
  /** de hussel / de toss / racket trekken */
  nounWithArticle: string;
  /** Husselen / Tossen / Racket trekken */
  verbCapital: string;
  /** Hussel / Toss / Racket trekken */
  nounCapital: string;
  /** husselavond / tossavond / racket trek-moment */
  eventName: string;
  /** Husselavond / Tossavond / Racket trek-moment */
  eventNameCapital: string;
  /** hussel-sessie / toss-sessie / racket trek-sessie */
  sessionName: string;
  /** raw key for matching */
  key: GameFormatKey;
}

const FORMATS: Record<GameFormatKey, GameFormatForms> = {
  'hussel': {
    verb: 'husselen',
    noun: 'hussel',
    nounWithArticle: 'de hussel',
    verbCapital: 'Husselen',
    nounCapital: 'Hussel',
    eventName: 'husselmoment',
    eventNameCapital: 'Husselmoment',
    sessionName: 'hussel-sessie',
    key: 'hussel',
  },
  'toss': {
    verb: 'tossen',
    noun: 'toss',
    nounWithArticle: 'de toss',
    verbCapital: 'Tossen',
    nounCapital: 'Toss',
    eventName: 'tossmoment',
    eventNameCapital: 'Tossmoment',
    sessionName: 'toss-sessie',
    key: 'toss',
  },
  'racket-trekken': {
    verb: 'racket trekken',
    noun: 'racket trekken',
    nounWithArticle: 'racket trekken',
    verbCapital: 'Racket trekken',
    nounCapital: 'Racket trekken',
    eventName: 'racket trek-moment',
    eventNameCapital: 'Racket trek-moment',
    sessionName: 'racket trek-sessie',
    key: 'racket-trekken',
  },
};

const DEFAULT_FORMAT = FORMATS['hussel'];

export const useGameFormat = () => {
  const [format, setFormat] = useState<GameFormatForms>(DEFAULT_FORMAT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFormat();
  }, []);

  const loadFormat = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: profile } = await supabase
        .from('profiles')
        .select('club_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile?.club_id) { setLoading(false); return; }

      const { data: club } = await supabase
        .from('clubs_public')
        .select('game_format_name')
        .eq('id', profile.club_id)
        .maybeSingle();

      if (club?.game_format_name) {
        const key = club.game_format_name as GameFormatKey;
        setFormat(FORMATS[key] || DEFAULT_FORMAT);
      }
    } catch (error) {
      console.error('Error loading game format:', error);
    } finally {
      setLoading(false);
    }
  };

  return { format, loading, FORMATS };
};

/** Get format forms for a given key (static, no hook needed) */
export const getGameFormatForms = (key: string): GameFormatForms => {
  return FORMATS[key as GameFormatKey] || DEFAULT_FORMAT;
};
